/**
 * Per-route SEO tags (title, description, canonical URL, Open Graph). Static
 * tags shared by every route live in `index.html` — this updates the
 * per-route ones on each SPA navigation so crawlers and social previews see
 * page-specific metadata instead of the generic landing copy for every URL.
 */

export const SITE_URL = 'https://callbridge.game';

interface RouteSeo {
  title: string;
  description: string;
}

const DEFAULT_SEO: RouteSeo = {
  title: 'Call Bridge — Classic Card Game',
  description:
    'Call Bridge - Classic card game from Bangladesh. Play solo against AI or online with friends.',
};

const ROUTE_SEO: Record<string, RouteSeo> = {
  '/': {
    title: 'Call Bridge — Play Online',
    description:
      'Play Call Bridge online: challenge AI opponents solo or join multiplayer rooms with friends.',
  },
  '/login': {
    title: 'Log In — Call Bridge',
    description: 'Log in to your Call Bridge account to track your stats and play online.',
  },
  '/register': {
    title: 'Sign Up — Call Bridge',
    description:
      'Create a free Call Bridge account to play the classic card game online with friends.',
  },
  '/leaderboard': {
    title: 'Leaderboard — Call Bridge',
    description: 'See the top-ranked Call Bridge players and where you stand.',
  },
};

/** Updates `document.title`, the meta description, Open Graph tags, and the canonical link for `path`. */
export function updateSeoTags(path: string): void {
  const seo = ROUTE_SEO[path] ?? DEFAULT_SEO;
  const url = `${SITE_URL}${path}`;

  document.title = seo.title;

  setMeta('description', seo.description);
  setMeta('og:title', seo.title, 'property');
  setMeta('og:description', seo.description, 'property');
  setMeta('og:url', url, 'property');

  setCanonical(url);
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}
